import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { ListGroup, Alert, Button, Form } from 'react-bootstrap'
import { useFormik } from 'formik'
import * as yup from 'yup'
import useActions from 'hooks/use-actions'
import { ReactComponent as Star } from './star-solid.svg'

const ratingSchema = yup.object().shape({
    rating: yup.number().min(1).max(5).required(),
    comments: yup.string().nullable().optional(),
    introduceMe: yup.boolean().nullable().optional(),
    anonymous: yup.boolean().nullable().optional()
})

const StartupVote = ({ featureId, startup }) => { // connected, onConnect

    const { t } = useTranslation()
    const { actions, doAction } = useActions(featureId, startup?.id)
    const [submitted, setSubmitted] = useState(false)
    const [error, setError] = useState(null)

    useEffect(() => {
        if (!startup?.id) return
        setSubmitted(actions.find(action => action.type === 'vote' && action.entityId === startup?.id))
    }, []) // startup, actions

    const submitRating = useCallback(values => {
        if (!startup) return false
        doAction('vote', startup.id, values)
        .then(action => {
            setError()
            setSubmitted(action)
        })
        .catch(setError)
    }, [doAction, startup])

    const initialValues = {
        rating: null,
        comments: '',
        introduceMe: false,
        anonymous: false
    }

    const { values, errors, setFieldValue, handleSubmit } = useFormik({
        initialValues,
        validationSchema: ratingSchema,
        // validate: submitRating,
        onSubmit: submitRating
    })

    const handleStarClick = (rating) => {
        setFieldValue('rating', rating)
        // if (rating !== 5) setFieldValue('introduceMe', false)
    }

    const handleIntroChange = () => {
        setFieldValue('introduceMe', !values.introduceMe)
        if (values.anonymous) {
            setFieldValue('anonymous', false)
        }
    }

    const handleAnonymousChange = () => {
        setFieldValue('anonymous', !values.anonymous)
    }

    const handleReset = useCallback(() => {
        if (submitted) {
            setFieldValue('rating', submitted.data?.rating || null)
            setFieldValue('comments', submitted.data?.comments)
        }
        setSubmitted(false)
    }, [setFieldValue, submitted])

    const results = startup?.data
    
    return results ? (
        <div className='p-3 bg-white'>
            <h5 className='mb-2'>{t('averageRating')}</h5>
            <ListGroup>
                <ListGroup.Item className='text-center flex-grow-1 bg-dark'>
                    {/* <div className='text-muted'>{t('relevance')}</div> */}
                    {/* <div className='h5 m-0'>{results.rating}</div> */}
                    <Stars rating={results.rating} size='1.75rem' />
                </ListGroup.Item>
            </ListGroup>
        </div>
    ) : submitted ? (
        <div className='p-3 bg-white'>
            <div>
                <h5 className='mb-2 d-inline-block'>{t('yourRating')}</h5>
                <Button variant='link' size='sm' className='mt-n1 text-info' onClick={handleReset}>{t('changeRating')}</Button>
            </div>
            <ListGroup>
                <ListGroup.Item className='flex-grow-1 p-3'>
                    {/* <div className='display-3 m-0'>{submitted.data?.rating}</div> */}
                    <Stars rating={submitted.data?.rating} size='1.75rem' />
                </ListGroup.Item>
                {submitted.data?.comments && (
                    <ListGroup.Item className='flex-grow-1 p-4 pb-3 pt-3'>
                        {/* <div className='text-muted small mb-1'>{t('commentsAndQuestions')}</div> */}
                        {submitted.data?.comments}
                    </ListGroup.Item>
                )}
            </ListGroup>
        </div>
    ) : (
        <form onSubmit={handleSubmit} noValidate>
            <div className='p-3 bg-white'>
                <div className='mb-2'>
                    {/* <h4>{t('rating')}</h4> */}
                    <div className='mb-1 small'>{t('ratingStarLabel')}</div>
                    <ListGroup horizontal>
                        {/* variant={values.rating === 1 ? 'primary' : null} */}
                        <ListGroup.Item className='p-0 flex-grow-1 bg-white'>
                            <label className='py-2 lh-0 text-center h5 m-0 d-block' style={{ cursor: 'pointer', color: values.rating >= 1 ? 'orange' : '#e5e5e5' }}>
                                <Star style={{ height: '1.75rem', width: '1.75rem', fill: 'currentcolor' }} />
                                <input type='radio' name='rating' value='1' className='d-none' onChange={handleStarClick.bind(null, 1)} />
                            </label>
                        </ListGroup.Item>
                        <ListGroup.Item className='p-0 flex-grow-1 bg-white'>
                            <label className='py-2 lh-0 text-center h5 m-0 d-block' style={{ cursor: 'pointer', color: values.rating >= 2 ? 'orange' : '#e5e5e5' }}>
                                <Star style={{ height: '1.75rem', width: '1.75rem', fill: 'currentcolor' }} />
                                <input type='radio' name='rating' value='2' className='d-none' onChange={handleStarClick.bind(null, 2)} />
                            </label>
                        </ListGroup.Item>
                        <ListGroup.Item className='p-0 flex-grow-1 bg-white'>
                            <label className='py-2 lh-0 text-center h5 m-0 d-block' style={{ cursor: 'pointer', color: values.rating >= 3 ? 'orange' : '#e5e5e5' }}>
                                <Star style={{ height: '1.75rem', width: '1.75rem', fill: 'currentcolor' }} />
                                <input type='radio' name='rating' value='3' className='d-none' onChange={handleStarClick.bind(null, 3)} />
                            </label>
                        </ListGroup.Item>
                        <ListGroup.Item className='p-0 flex-grow-1 bg-white'>
                            <label className='py-2 lh-0 text-center h5 m-0 d-block' style={{ cursor: 'pointer', color: values.rating >= 4 ? 'orange' : '#e5e5e5' }}>
                                <Star style={{ height: '1.75rem', width: '1.75rem', fill: 'currentcolor' }} />
                                <input type='radio' name='rating' value='4' className='d-none' onChange={handleStarClick.bind(null, 4)} />
                            </label>
                        </ListGroup.Item>
                        <ListGroup.Item className='p-0 flex-grow-1 bg-white'>
                            <label className='py-2 lh-0 text-center h5 m-0 d-block' style={{ cursor: 'pointer', color: values.rating >= 5 ? 'orange' : '#e5e5e5' }}>
                                <Star style={{ height: '1.75rem', width: '1.75rem', fill: 'currentcolor' }} />
                                <input type='radio' name='rating' value='5' className='d-none' onChange={handleStarClick.bind(null, 5)} />
                            </label>
                        </ListGroup.Item>
                    </ListGroup>
                    {errors?.rating && <div className='text-danger small mb-n2'>{t('required')}</div>}
                </div>
                <div className='mb-2'>
                    <div className='mb-1 small'>{t('ratingCommentLabel')}</div>
                    <Form.Control as='textarea' style={{ resize: 'none' }} rows={3} defaultValue={values.comments} onChange={e => setFieldValue('comments', e.target.value)} />
                </div>
                {/* {values.rating === 5 && <div className='mb-2'>
                    <Form.Check type='checkbox' id='introduceMe' name='introduceMe' value={values.introduceMe} onChange={(e) => setFieldValue('introduceMe', e.target.checked)} label='Please introduce me to a representative from this partner' />
                </div>} */}
                <div className='mb-2'>
                     {/* Please have Inspire... */}
                    <Form.Check className='small' type='checkbox' label={'Please have Inspire introduce me to this partner'} id={'startupRatingConnect'} checked={values.introduceMe} onChange={handleIntroChange} />
                    <Form.Check className={'small' + (values.introduceMe ? ' text-muted' : '')} type='checkbox' disabled={values.introduceMe} label={'Make my feedback anonymous'} id={'startupRatingAnonymous'} checked={values.anonymous} onChange={handleAnonymousChange} />
                    {/* Share my contact info with this presenter */}
                </div>
                {error && <Alert variant='warning'>{error.message}</Alert>}
                <div>
                    <Button type='submit' size='lg' variant={submitted ? 'secondary' : 'primary'} className='mt-2' style={{ cursor: submitted ? 'default' : 'pointer' }} disabled={!values.rating}>
                        {t('submit')}
                    </Button>
                </div>
            </div>
        </form>
    )

}

export default StartupVote

const Stars = ({ rating, size }) => {
    const value = Math.round(rating)
    return (
        <div className='d-flex justify-content-start'>
            <div className='px-1' style={{ color: value >= 1 ? 'orange' : '#ccc' }}>
                <Star style={{ height: size, width: size, fill: 'currentcolor' }} />
            </div>
            <div className='px-1' style={{ color: value >= 2 ? 'orange' : '#ccc' }}>
                <Star style={{ height: size, width: size, fill: 'currentcolor' }} />
            </div>
            <div className='px-1' style={{ color: value >= 3 ? 'orange' : '#ccc' }}>
                <Star style={{ height: size, width: size, fill: 'currentcolor' }} />
            </div>
            <div className='px-1' style={{ color: value >= 4 ? 'orange' : '#ccc' }}>
                <Star style={{ height: size, width: size, fill: 'currentcolor' }} />
            </div>
            <div className='px-1' style={{ color: value >= 5 ? 'orange' : '#ccc' }}>
                <Star style={{ height: size, width: size, fill: 'currentcolor' }} />
            </div>
        </div>
    )
}